/**
 * Vendor-clean MapLibre base style, built entirely from the geo-core TileSource
 * (M2) + the cartographic tokens (M1). No vendor/style URL is ever hardcoded
 * here (criterion 11) — every URL comes from the injected TileSource (`spec`,
 * `glyphs`, `sprites`); a test asserts the only host in the output is the one
 * the descriptor injected. The designed "modern warm" cartography — turquoise
 * water, mint land, fresh green parks, friendly white roads with one warm
 * motorway ribbon — is colored from `map.*` tokens against the OpenMapTiles
 * vector schema (the default OpenFreeMap source), with the earlier Protomaps
 * schema kept for self-hosted PMTiles archives. Place labels render whenever
 * the TileSource provides a glyph endpoint.
 */

import type { TileSource } from "@/lib/geo/types";
import type { MapTokens } from "./mapTokens";

/**
 * Selectable basemaps (MAP-102). `light`/`dark` + three curated token-variant
 * vector styles, plus `satellite` (keyless Esri raster with a vector label
 * overlay). `mapspec.basemap` stays a free-form string; unknown values fall back
 * to `light`, so old snapshots and NL edits never break.
 */
export type Basemap = "light" | "dark" | "minimal" | "muted" | "contrast" | "satellite";

/** The vector (non-satellite) variants — the ones warmLayers paints. */
export type BasemapVariant = "light" | "dark" | "minimal" | "muted" | "contrast";

const VECTOR_VARIANTS: readonly BasemapVariant[] = ["light", "dark", "minimal", "muted", "contrast"];

/** All selectable basemaps, in picker order. */
export const BASEMAPS: readonly Basemap[] = ["light", "dark", "minimal", "muted", "contrast", "satellite"];

/** Human labels for the picker. */
export const BASEMAP_LABELS: Record<Basemap, string> = {
  light: "Light",
  dark: "Dark",
  minimal: "Minimal",
  muted: "Muted",
  contrast: "Contrast",
  satellite: "Satellite",
};

/** Normalize any stored/NL basemap string to a known Basemap (default light). */
export function normalizeBasemap(value: string | undefined): Basemap {
  return (BASEMAPS as readonly string[]).includes(value ?? "") ? (value as Basemap) : "light";
}

/**
 * Paid-only basemaps (MAP-102 follow-up). Satellite composites licensed Esri
 * imagery, so it's gated to paid plans; the vector variants are free. Kept as a
 * data list rather than a hardcoded `=== "satellite"` so a future paid basemap
 * joins the gate in one place.
 */
export const PAID_BASEMAPS: readonly Basemap[] = ["satellite"];

/** The free basemap a paid choice degrades to when the owner isn't entitled. */
export const FREE_BASEMAP_FALLBACK: Basemap = "light";

/** True when `value` normalizes to a paid-only basemap. */
export function isPaidBasemap(value: string | undefined): boolean {
  return PAID_BASEMAPS.includes(normalizeBasemap(value));
}

/**
 * The ONE authoritative "may this account use this basemap" rule. A free/
 * unentitled owner's paid basemap degrades to {@link FREE_BASEMAP_FALLBACK};
 * every free basemap (and any choice by an entitled owner) passes through
 * unchanged. The client picker uses it to lock tiles and the save chokepoint
 * (`buildServerSnapshot`) uses it to coerce the persisted spec, so a downgraded
 * owner never renders paid imagery on any surface.
 */
export function clampBasemapForEntitlement(value: string | undefined, entitled: boolean): Basemap {
  const basemap = normalizeBasemap(value);
  return entitled || !PAID_BASEMAPS.includes(basemap) ? basemap : FREE_BASEMAP_FALLBACK;
}

/**
 * Per-variant paint knobs for the vector styles (MAP-102). Each is a deliberate,
 * token-sourced treatment — NOT a random recolor:
 *  - light/dark: the original warm cartography.
 *  - minimal: a quiet canvas — drop the landcover/land-use/park washes and
 *    hairline the roads so plotted points are the loudest thing on the map.
 *  - muted: soft/desaturated — every fill + road dialed back.
 *  - contrast: high-legibility — bolder roads, stronger boundaries + labels.
 */
interface VariantKnobs {
  dark: boolean;
  showFills: boolean; // landcover / landuse / park washes
  roadScale: number; // multiplies road widths
  roadOpacity: number; // multiplies road opacity
  boundaryOpacity: number;
  labelBoost: number; // multiplies label size/halo emphasis
  minimalLabels: boolean; // cities only, no neighbourhoods
}

const VARIANT_KNOBS: Record<BasemapVariant, VariantKnobs> = {
  light: { dark: false, showFills: true, roadScale: 1, roadOpacity: 1, boundaryOpacity: 1, labelBoost: 1, minimalLabels: false },
  dark: { dark: true, showFills: false, roadScale: 1, roadOpacity: 1, boundaryOpacity: 1, labelBoost: 1, minimalLabels: false },
  minimal: { dark: false, showFills: false, roadScale: 0.7, roadOpacity: 0.7, boundaryOpacity: 0.5, labelBoost: 0.9, minimalLabels: true },
  muted: { dark: false, showFills: true, roadScale: 0.85, roadOpacity: 0.7, boundaryOpacity: 0.6, labelBoost: 0.9, minimalLabels: false },
  contrast: { dark: false, showFills: true, roadScale: 1.25, roadOpacity: 1, boundaryOpacity: 1, labelBoost: 1.15, minimalLabels: false },
};

/** Minimal MapLibre StyleSpecification shape (renderer casts to the real type). */
export interface MapStyle {
  version: 8;
  name: string;
  glyphs?: string;
  sprite?: string;
  sources: Record<string, unknown>;
  layers: unknown[];
}

/**
 * Glyph fontstacks requested by symbol layers. These are the OpenMapTiles-
 * standard stacks — schema data like source-layer names, not vendor URLs.
 * Every glyph endpoint the registry wires must host them (OpenFreeMap does;
 * a self-hosted archive must ship the same stacks).
 */
export const GLYPH_FONTSTACK_REGULAR = "Noto Sans Regular";
export const GLYPH_FONTSTACK_BOLD = "Noto Sans Bold";

/** OpenMapTiles-schema vector sources keep the conventional source name. */
const VECTOR_SOURCE_ID = "openmaptiles";
const BASEMAP_SOURCE_ID = "basemap";

function sourceIdFor(type: TileSource["type"]): string {
  return type === "vector" ? VECTOR_SOURCE_ID : BASEMAP_SOURCE_ID;
}

/** Turn the TileSource into a MapLibre source object without inventing any URL. */
function sourceFromTile(tile: TileSource): Record<string, unknown> {
  if (tile.type === "raster") {
    return { type: "raster", tiles: [tile.spec], tileSize: 256, attribution: tile.attribution() };
  }
  if (tile.type === "pmtiles") {
    // pmtiles:// protocol is registered client-side by the renderer.
    return { type: "vector", url: `pmtiles://${tile.spec}`, attribution: tile.attribution() };
  }
  // vector: spec is a TileJSON URL (OpenMapTiles schema).
  return { type: "vector", url: tile.spec, attribution: tile.attribution() };
}

/**
 * The modern-warm cartography against the OpenMapTiles schema, colored from
 * tokens (the ported /concepts/modern-warm map style). Layers whose
 * source-layer is absent simply don't render — no error — so the set is safe
 * against any archive. Label layers are appended only when glyphs exist.
 */
/** Scale a two-stop [z0,w0, z1,w1] line-width interpolate by `k` (keeps the stops). */
function scaledWidth(z0: number, w0: number, z1: number, w1: number, k: number): unknown {
  return ["interpolate", ["linear"], ["zoom"], z0, w0 * k, z1, w1 * k];
}

/**
 * Place-label symbol layers over the OpenMapTiles vector source. Reused by both
 * the vector styles and the satellite style (labels ride on top of the imagery).
 * `overImagery` flips to light text + a strong dark halo so names stay legible
 * on photography; `minimal` drops the neighbourhood tier for a quieter map.
 */
function labelLayers(t: MapTokens, opts: { dark: boolean; overImagery: boolean; minimal: boolean; boost: number }): unknown[] {
  const light = opts.dark || opts.overImagery;
  const ink = light ? t.label.halo : t.label.ink;
  const secondary = light ? t.label.halo : t.label.secondary;
  const halo = light ? t.darkField : t.label.halo;
  const haloBase = opts.overImagery ? 2.0 : 1.2;
  const layers: unknown[] = [];
  if (!opts.minimal) {
    layers.push({
      id: "label-neighbourhood",
      type: "symbol",
      source: VECTOR_SOURCE_ID,
      "source-layer": "place",
      minzoom: 10.5,
      filter: ["in", ["get", "class"], ["literal", ["suburb", "neighbourhood"]]],
      layout: {
        "text-field": ["coalesce", ["get", "name:en"], ["get", "name"]],
        "text-font": [GLYPH_FONTSTACK_REGULAR],
        "text-size": 10.5 * opts.boost,
        "text-letter-spacing": 0.04,
      },
      paint: {
        "text-color": secondary,
        "text-opacity": 0.8,
        "text-halo-color": halo,
        "text-halo-width": haloBase,
      },
    });
  }
  layers.push({
    id: "label-city",
    type: "symbol",
    source: VECTOR_SOURCE_ID,
    "source-layer": "place",
    filter: ["in", ["get", "class"], ["literal", ["city", "town"]]],
    layout: {
      "text-field": ["coalesce", ["get", "name:en"], ["get", "name"]],
      "text-font": [GLYPH_FONTSTACK_BOLD],
      "text-size": 12.5 * opts.boost,
    },
    paint: { "text-color": ink, "text-halo-color": halo, "text-halo-width": haloBase + 0.2 },
  });
  return layers;
}

function warmLayers(t: MapTokens, variant: BasemapVariant, hasGlyphs: boolean): unknown[] {
  const src = VECTOR_SOURCE_ID;
  const k = VARIANT_KNOBS[variant];
  const dark = k.dark;

  // Dark field: deep teal-ink ground, dimmed turquoise water, ghosted roads,
  // light labels — all still token-sourced (only opacity/scale varies by variant).
  const ground = dark ? t.darkField : t.tile.land;
  const road = dark ? t.tile.boundary : t.tile.roadFill;
  const motorway = dark ? t.tile.boundary : t.tile.motorway;

  const layers: unknown[] = [
    { id: "land", type: "background", paint: { "background-color": ground } },
  ];

  // Landcover / land-use / park washes — the "showFills" knob drops them for the
  // quiet minimal style and (as before) for dark.
  if (k.showFills && !dark) {
    layers.push(
      {
        id: "landcover",
        type: "fill",
        source: src,
        "source-layer": "landcover",
        filter: ["in", ["get", "class"], ["literal", ["wood", "grass", "farmland"]]],
        paint: { "fill-color": t.tile.landcover, "fill-opacity": 0.5 },
      },
      {
        id: "landuse-urban",
        type: "fill",
        source: src,
        "source-layer": "landuse",
        filter: ["in", ["get", "class"], ["literal", ["residential", "commercial", "industrial", "suburb"]]],
        paint: { "fill-color": t.tile.building, "fill-opacity": 0.65 },
      },
      {
        id: "park",
        type: "fill",
        source: src,
        "source-layer": "park",
        paint: { "fill-color": t.tile.greenspace, "fill-opacity": 0.85 },
      },
    );
  }

  layers.push(
    {
      id: "water",
      type: "fill",
      source: src,
      "source-layer": "water",
      paint: { "fill-color": t.tile.water, "fill-opacity": dark ? 0.3 : 1 },
    },
    {
      id: "waterway",
      type: "line",
      source: src,
      "source-layer": "waterway",
      paint: { "line-color": t.tile.water, "line-width": 1.2 * k.roadScale, "line-opacity": dark ? 0.3 : 1 },
    },
    {
      id: "road-minor",
      type: "line",
      source: src,
      "source-layer": "transportation",
      minzoom: 11.5,
      filter: ["in", ["get", "class"], ["literal", ["minor", "service"]]],
      paint: {
        "line-color": road,
        "line-width": scaledWidth(11.5, 0.4, 14, 1.6, k.roadScale),
        "line-opacity": (dark ? 0.35 : 0.85) * k.roadOpacity,
      },
    },
    {
      id: "road-mid",
      type: "line",
      source: src,
      "source-layer": "transportation",
      minzoom: 9.5,
      filter: ["in", ["get", "class"], ["literal", ["secondary", "tertiary"]]],
      paint: {
        "line-color": road,
        "line-width": scaledWidth(9.5, 0.6, 13, 2.6, k.roadScale),
        "line-opacity": (dark ? 0.4 : 1) * k.roadOpacity,
      },
    },
    {
      id: "road-primary",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: ["in", ["get", "class"], ["literal", ["primary", "trunk"]]],
      paint: {
        "line-color": road,
        "line-width": scaledWidth(8, 0.9, 13, 4.2, k.roadScale),
        "line-opacity": (dark ? 0.45 : 1) * k.roadOpacity,
      },
    },
    {
      id: "road-motorway",
      type: "line",
      source: src,
      "source-layer": "transportation",
      filter: ["==", ["get", "class"], "motorway"],
      paint: {
        "line-color": motorway,
        "line-width": scaledWidth(8, 1.1, 13, 4.6, k.roadScale),
        "line-opacity": (dark ? 0.5 : 1) * k.roadOpacity,
      },
    },
    {
      id: "boundary-state",
      type: "line",
      source: src,
      "source-layer": "boundary",
      filter: ["all", ["==", ["get", "admin_level"], 4], ["==", ["get", "maritime"], 0]],
      paint: {
        "line-color": t.tile.boundary,
        "line-width": 0.8 * k.roadScale,
        "line-opacity": 0.6 * k.boundaryOpacity,
        "line-dasharray": [4, 3],
      },
    },
    {
      id: "boundary-country",
      type: "line",
      source: src,
      "source-layer": "boundary",
      filter: ["all", ["==", ["get", "admin_level"], 2], ["==", ["get", "maritime"], 0]],
      paint: { "line-color": t.tile.boundary, "line-width": 1.1 * k.roadScale, "line-opacity": 0.8 * k.boundaryOpacity },
    },
  );

  if (hasGlyphs) {
    layers.push(...labelLayers(t, { dark, overImagery: false, minimal: k.minimalLabels, boost: k.labelBoost }));
  }

  return layers;
}

/**
 * Designed vector fills/lines against the Protomaps basemap schema (self-hosted
 * PMTiles archives), colored from tokens. Kept beside the OpenMapTiles set so a
 * registry swap back to the pmtiles source still renders the designed ground.
 */
function atlasLayers(t: MapTokens, variant: BasemapVariant): unknown[] {
  const src = BASEMAP_SOURCE_ID;
  const paper = variant === "dark" ? t.darkField : t.tile.land;
  const water = variant === "dark" ? t.darkField : t.tile.water;
  const green = variant === "dark" ? t.darkField : t.tile.greenspace;
  const building = variant === "dark" ? t.darkField : t.tile.building;
  const casing = t.tile.roadCasing;
  const roadFill = variant === "dark" ? t.tile.boundary : t.tile.roadFill;

  return [
    { id: "bg", type: "background", paint: { "background-color": paper } },
    { id: "earth", type: "fill", source: src, "source-layer": "earth", paint: { "fill-color": paper } },
    { id: "landuse", type: "fill", source: src, "source-layer": "landuse", paint: { "fill-color": green, "fill-opacity": 0.6 } },
    { id: "water", type: "fill", source: src, "source-layer": "water", paint: { "fill-color": water } },
    { id: "buildings", type: "fill", source: src, "source-layer": "buildings", minzoom: 13, paint: { "fill-color": building, "fill-opacity": 0.7 } },
    {
      id: "roads-casing",
      type: "line",
      source: src,
      "source-layer": "roads",
      paint: { "line-color": casing, "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.5, 16, 6] },
    },
    {
      id: "roads-fill",
      type: "line",
      source: src,
      "source-layer": "roads",
      paint: { "line-color": roadFill, "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.25, 16, 4] },
    },
    {
      id: "boundaries",
      type: "line",
      source: src,
      "source-layer": "boundaries",
      paint: { "line-color": t.tile.boundary, "line-dasharray": [2, 2], "line-width": 0.75 },
    },
  ];
}

/**
 * Serializable tile descriptor — the client-safe shape a server component passes
 * to the client renderer (resolved from geo-core via `resolveTileDescriptor` in
 * ./server, which must NOT be imported client-side). Attribution is the resolved
 * string, not a method.
 */
export interface TileDescriptor {
  type: TileSource["type"];
  spec: string;
  glyphs: string;
  sprites: string;
  attribution: string;
}

/**
 * The satellite style (MAP-102): the Esri raster imagery with the OpenMapTiles
 * place labels composited ON TOP (imagery ships no labels). Two sources —
 * `basemap` (raster) + `openmaptiles` (vector, labels only). Labels use the
 * over-imagery treatment (light text, strong dark halo). If the vector source /
 * glyphs are unavailable, the raster alone still renders (just unlabeled).
 */
function satelliteStyle(satellite: TileDescriptor, vector: TileDescriptor, tokens: MapTokens): MapStyle {
  const sources: Record<string, unknown> = {
    [BASEMAP_SOURCE_ID]: { type: "raster", tiles: [satellite.spec], tileSize: 256, attribution: satellite.attribution },
  };
  const layers: unknown[] = [
    { id: "bg", type: "background", paint: { "background-color": tokens.darkField } },
    { id: "raster", type: "raster", source: BASEMAP_SOURCE_ID },
  ];
  const canLabel = vector.spec !== "" && vector.glyphs !== "";
  if (canLabel) {
    sources[VECTOR_SOURCE_ID] = { type: "vector", url: vector.spec, attribution: vector.attribution };
    layers.push(...labelLayers(tokens, { dark: true, overImagery: true, minimal: false, boost: 1 }));
  }
  const style: MapStyle = { version: 8, name: "plotzio-satellite", sources, layers };
  if (canLabel) style.glyphs = vector.glyphs;
  return style;
}

/**
 * Build a style from a serialized descriptor (client-safe; no geo-core import).
 * `opts.satellite` is the optional Esri raster descriptor; when `basemap` is
 * `satellite` and it's present, the satellite style is composited from both.
 */
export function styleFromDescriptor(
  d: TileDescriptor,
  tokens: MapTokens,
  opts: { basemap?: string; satellite?: TileDescriptor | null } = {},
): MapStyle {
  const basemap = normalizeBasemap(opts.basemap);
  if (basemap === "satellite" && opts.satellite && opts.satellite.spec !== "") {
    return satelliteStyle(opts.satellite, d, tokens);
  }
  // A satellite request with no usable satellite source degrades to light vector.
  const variant: BasemapVariant = VECTOR_VARIANTS.includes(basemap as BasemapVariant)
    ? (basemap as BasemapVariant)
    : "light";

  // No archive available (spec resolved empty server-side): the designed paper
  // ground still paints as a finished muted surface — B4's placeholder rule —
  // with no tile source to request (and so nothing to 404 against).
  if (d.spec === "") {
    const style: MapStyle = {
      version: 8,
      name: `plotzio-atlas-${variant}`,
      sources: {},
      layers: [
        {
          id: "bg",
          type: "background",
          paint: { "background-color": variant === "dark" ? tokens.darkField : tokens.tile.land },
        },
      ],
    };
    if (d.glyphs) style.glyphs = d.glyphs;
    if (d.sprites) style.sprite = d.sprites;
    return style;
  }
  return buildMapStyle(
    { id: "tile", type: d.type, spec: d.spec, glyphs: d.glyphs, sprites: d.sprites, billingModel: "none", attribution: () => d.attribution },
    tokens,
    { basemap: variant },
  );
}

export function buildMapStyle(
  tile: TileSource,
  tokens: MapTokens,
  opts: { basemap?: BasemapVariant } = {},
): MapStyle {
  const variant = opts.basemap ?? "light";
  const sourceId = sourceIdFor(tile.type);
  const layers =
    tile.type === "raster"
      ? [
          { id: "bg", type: "background", paint: { "background-color": variant === "dark" ? tokens.darkField : tokens.tile.land } },
          { id: "raster", type: "raster", source: sourceId },
        ]
      : tile.type === "vector"
        ? warmLayers(tokens, variant, tile.glyphs !== "")
        : atlasLayers(tokens, variant);

  const style: MapStyle = {
    version: 8,
    name: `plotzio-atlas-${variant}`,
    sources: { [sourceId]: sourceFromTile(tile) },
    layers,
  };
  if (tile.glyphs) style.glyphs = tile.glyphs;
  if (tile.sprites) style.sprite = tile.sprites;
  return style;
}
